import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import chatgptAdsLogo from '../../logos/chatgpt-ads.png';
import config from '../../config';

const API_URL = config.API_URL;

/**
 * Le formulaire de connexion à ChatGPT Ads : une clé API, un compte.
 *
 * ## Pourquoi un composant à part
 *
 * Ce formulaire vivait à l'intérieur de `ConnectorsView`, dans `App.js`, avec ses cinq états et
 * ses deux gestionnaires. Il n'était donc atteignable que depuis l'écran Connecteurs. Le
 * parcours d'installation devait pouvoir l'ouvrir lui aussi : le recopier aurait fait une
 * deuxième version à tenir d'accord avec la première, pour un formulaire qui manipule une clé
 * secrète — l'endroit où une divergence coûte le plus cher.
 *
 * ## Le protocole, inchangé
 *
 * `POST …/test` valide la clé **et rend le compte qu'elle ouvre** ; `POST …/connect` enregistre.
 * Le bouton de connexion reste fermé tant que le test n'a pas réussi : on n'enregistre jamais
 * une clé dont on n'a pas vu le compte. Une clé déjà connectée est reconnue comme telle et ne
 * peut pas être enregistrée deux fois.
 *
 * Le connecteur crée **son compte au moment de la connexion** — une clé vaut un compte
 * publicitaire. Celui qui l'appelle n'a donc pas d'étape de choix de comptes à proposer ensuite.
 *
 * @param {boolean} ouvert
 * @param {() => void} onFermer
 * @param {string} token - JWT de l'utilisateur
 * @param {(info: {compte: object}) => void} onConnecte - appelé après enregistrement réussi
 */
function FormulaireChatGptAds({ ouvert, onFermer, token, onConnecte }) {
  const [cle, setCle] = useState('');
  const [resultat, setResultat] = useState(null);
  const [test, setTest] = useState(false);
  const [connexion, setConnexion] = useState(false);

  const fermer = () => {
    setResultat(null);
    onFermer?.();
  };

  const tester = async () => {
    if (!cle.trim()) {
      setResultat({ success: false, error: 'Saisissez la clé API' });
      return;
    }
    setTest(true);
    setResultat(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/openai-ads/test`,
        { apiKey: cle.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setResultat(data);
    } catch (error) {
      setResultat({
        success: false,
        error: error.response?.data?.error || 'Impossible de joindre le serveur'
      });
    } finally {
      setTest(false);
    }
  };

  const connecter = async () => {
    setConnexion(true);
    try {
      await axios.post(`${API_URL}/api/auth/openai-ads/connect`,
        { apiKey: cle.trim() },
        { headers: { Authorization: `Bearer ${token}` } });

      const compte = resultat?.account;
      setCle('');
      setResultat(null);
      onFermer?.();
      onConnecte?.({ compte });
    } catch (error) {
      setResultat({
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la connexion'
      });
    } finally {
      setConnexion(false);
    }
  };

  if (!ouvert) return null;

  const connexionImpossible = !resultat?.success || resultat?.alreadyConnected || connexion;

  return ReactDOM.createPortal(
    <div
      onClick={fermer}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: '16px', width: '100%', maxWidth: '540px',
          maxHeight: '90vh', overflowY: 'auto', padding: '24px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
          <img src={chatgptAdsLogo} alt="" style={{ width: '32px', height: '32px' }} />
          <h2 style={{ margin: 0, fontSize: '18px' }}>Connecter ChatGPT Ads</h2>
        </div>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 18px 0', lineHeight: 1.5 }}>
          Une clé API donne accès à <strong>un seul compte publicitaire</strong>. Pour
          plusieurs clients, connectez une clé par compte.
        </p>

        <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
          Clé API OpenAI Ads
        </label>
        <input
          type="password"
          value={cle}
          onChange={(e) => { setCle(e.target.value); setResultat(null); }}
          placeholder="sk-svcacct-…"
          autoComplete="off"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 12px',
            border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
          }}
        />
        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '6px 0 0 0' }}>
          La clé est chiffrée avant d'être enregistrée et n'est jamais réaffichée.
        </p>

        {/* Le compte est montré avant l'enregistrement : on connecte ce qu'on a vu */}
        {resultat?.success && (
          <div style={{
            marginTop: '16px', padding: '12px 14px', borderRadius: '8px',
            background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '13px'
          }}>
            <div style={{ fontWeight: 600, color: '#166534' }}>{resultat.account?.name}</div>
            <div style={{ color: '#15803d', fontSize: '12px', marginTop: '4px' }}>
              {resultat.account?.id}
              {resultat.account?.currency ? ` · ${resultat.account.currency}` : ''}
              {resultat.account?.timeZone ? ` · ${resultat.account.timeZone}` : ''}
            </div>
            {resultat.alreadyConnected && (
              <div style={{ color: '#b45309', fontSize: '12px', marginTop: '6px' }}>
                Ce compte est déjà connecté.
              </div>
            )}
            {resultat.encryptionActive === false && (
              <div style={{ color: '#b45309', fontSize: '12px', marginTop: '6px' }}>
                Le chiffrement des secrets n'est pas configuré sur ce serveur : la clé
                sera stockée en clair.
              </div>
            )}
          </div>
        )}

        {resultat && !resultat.success && (
          <div style={{
            marginTop: '16px', padding: '12px 14px', borderRadius: '8px',
            background: '#fef2f2', border: '1px solid #fecaca',
            fontSize: '13px', color: '#b91c1c'
          }}>
            {resultat.error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button
            onClick={fermer}
            style={{
              padding: '9px 16px', background: 'white', border: '1px solid #d1d5db',
              borderRadius: '8px', fontSize: '13px', cursor: 'pointer'
            }}
          >
            Annuler
          </button>
          <button
            onClick={tester}
            disabled={test || !cle.trim()}
            style={{
              padding: '9px 16px', background: 'white', border: '1px solid #d1d5db',
              borderRadius: '8px', fontSize: '13px',
              cursor: test || !cle.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {test ? 'Vérification…' : 'Tester la connexion'}
          </button>
          <button
            onClick={connecter}
            // La connexion n'est offerte qu'après une validation réussie : on n'enregistre
            // jamais une clé dont on n'a pas vu le compte.
            disabled={connexionImpossible}
            style={{
              padding: '9px 18px', background: '#1a1a1a', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '13px',
              cursor: connexionImpossible ? 'not-allowed' : 'pointer',
              opacity: connexionImpossible ? 0.5 : 1
            }}
          >
            {connexion ? 'Connexion…' : 'Connecter'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default FormulaireChatGptAds;
